import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import ProtectedRoute from './components/auth/ProtectedRoute.js';
import SessionTimeoutNotification from './components/common/SessionTimeoutNotification';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';

// Import all page components
import AboutUsPage from './pages/public/AboutUsPage';
import ContactUsPage from './pages/public/ContactUsPage';
import EmailVerificationPage from './pages/public/EmailVerificationPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import HelpFAQPage from './pages/public/HelpFAQPage';
import HomePage from './pages/public/HomePage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage';
import SignInPage from './pages/public/SignInPage';
import SignUpPage from './pages/public/SignUpPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import UserDashboard from './pages/user/UserDashboard';
// import ProfileWizard from './pages/user/ProfileWizard';
import gsap from 'gsap';
import { ScrollTrigger, SplitText } from 'gsap/all';
import AdminLayout from './components/admin/AdminLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminAuditStatistics from './pages/admin/AdminAuditStatistics';
import AdminAuditTrail from './pages/admin/AdminAuditTrail';
import AdminAuditTrailDetailsPage from './pages/admin/AdminAuditTrailDetailsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminJobApplicationDetailPage from './pages/admin/AdminJobApplicationDetailPage';
import AdminJobApplicationPage from './pages/admin/AdminJobApplicationPage';
import AdminJobDetails from './pages/admin/AdminJobDetails';
import AdminPaymentDetailsPage from './pages/admin/AdminPaymentDetailsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminRecentActivity from './pages/admin/AdminRecentActivity';
import AdminSubscriptionDetails from './pages/admin/AdminSubscriptionDetails';
import AdminSubscriptionPlanDetails from './pages/admin/AdminSubscriptionPlanDetails';
import AdminSubscriptionPlans from './pages/admin/AdminSubscriptionPlans';
import AdminSubscriptionsListPage from './pages/admin/AdminSubscriptionsListPage';
import AdminUserDetailsPage from './pages/admin/AdminUserDetailsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import JobListingManagementPage from './pages/admin/JobListingManagementPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import Chat from './pages/chat/Chat';
import StripePaymentPage from './pages/pricing/StripePaymentPage';
import StripePaymentSuccessPage from './pages/pricing/StripePaymentSuccessPage';
import SubscriptionPaymentPage from './pages/pricing/SubscriptionPaymentPage';
import SubscriptionPendingPage from './pages/pricing/SubscriptionPendingPage';
import SubscriptionPricingPage from './pages/pricing/SubscriptionPricingPage';
import SubscriptionSuccessPage from './pages/pricing/SubscriptionSuccessPage';
import JobApplicationForm from './pages/professional/JobApplicationForm';
import JobDetailsPage from './pages/professional/JobDetailsPage';
import JobSearchPage from './pages/professional/JobSearchPage';
import MyApplicationsPage from './pages/professional/MyApplicationsPage';
import RecommendationsPage from './pages/professional/RecommendationsPage';
import SavedJobsPage from './pages/professional/SavedJobsPage';
import SharedRecommendationPage from './pages/public/SharedRecommendationPage';
import CandidateProfilePage from './pages/recruiter/CandidateProfilePage';
import CompanyProfilePage from './pages/recruiter/CompanyProfilePage';
import ManageJobListingsPage from './pages/recruiter/ManageJobListingsPage';
import PostJobWizard from './pages/recruiter/PostJobWizard';
import ProfessionalSearchPage from './pages/recruiter/ProfessionalSearchPage';
import RecruiterDashboard from './pages/recruiter/RecruiterDashboard';
import RecruiterRecommendationsPage from './pages/recruiter/RecruiterRecommendationsPage';
import SendRecommendationPage from './pages/recruiter/SendRecommendationPage';
import ViewApplicationsPage from './pages/recruiter/ViewApplicationsPage';
import AccountSettingsPage from './pages/user/AccountSettingsPage';
import ManageProfilePage from './pages/user/ManageProfilePage';
import NotificationsCenterPage from './pages/user/NotificationsCenterPage';
import VerificationStatusPage from './pages/user/VerificationStatusPage';
import { useAuthStore } from './store/authStore.js';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// This gatekeeper component is now robust against race conditions on refresh.
const DashboardGate = () => {
  const user = useAuthStore(s => s.user);
  const isLoading = useAuthStore(s => s.isLoading);
  const navigate = useNavigate();
  console.log('DashboardGate-user, isLoading:', user, isLoading);

  // If authentication is still in progress, we show a loading spinner.
  // This prevents any rendering attempt before the user's role is known.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Once loading is complete, we can safely check the user's role.
  if (user?.role === 'recruiter') {
    return <RecruiterDashboard onNavigate={navigate} />;
  }

  if (user?.role === 'professional') {
    return <UserDashboard />;
  }

  if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // The ProtectedRoute wrapper will handle the final redirect to /signin if the user is null
  // after loading, so we don't need an explicit redirect here. This prevents the bug.
  return null;
};

gsap.registerPlugin(ScrollTrigger, SplitText)

function App() {
  const user = useAuthStore(s => s.user);
  const isLoading = useAuthStore(s => s.isLoading);
  console.log('Applevel isLoading', isLoading)
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigate = (path) => navigate(path);

  const isDashboardPage = () => {
    const protectedPaths = [
      '/dashboard', '/profile-wizard', '/manage-profile', '/subscription-management',
      '/notifications', '/account-settings', '/job-application', '/job-search', '/job-details',
      '/my-applications', '/saved-jobs', '/post-job', '/manage-jobs', '/view-applications',
      '/find-professionals', '/candidate-profile', '/company-profile', '/admin', '/verification', '/pricing',
      '/subscription', '/chat', '/professional', '/recruiter'
    ];
    return protectedPaths.some(path => location.pathname.startsWith(path));
  };

  const isAdminPage = () => {
    return location.pathname.startsWith('/admin');
  };

  const isChatPage = () => {
    return location.pathname.startsWith('/chat')
  }

  // A top-level loading check for the very first load of the app.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-secondary-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-secondary-50 font-sans">
      <ScrollToTop />
      {/* Show session timeout notification for authenticated users */}
      {user && <SessionTimeoutNotification />}

      {isAdminPage() && user ? (
        // Admin pages use the layout which includes both sidebar and header
        <>
          {/* <AdminLayout /> */}
        </>
      ) : (
        // Non-admin, non-dashboard, non-logged-in: show public header
        !isDashboardPage() && <Header onNavigate={handleNavigate} />
      )}
      {/* ${isDashboardPage() && user ? 'pt-20' : 'pt-[60px]'} */}
      <main className={``}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage onNavigate={handleNavigate} />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/help" element={<HelpFAQPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/recommendations/shared/:token" element={<SharedRecommendationPage />} />

          {/* Auth Routes */}
          <Route path="/signin" element={<SignInPage onNavigate={handleNavigate} />} />
          <Route path="/signup" element={<SignUpPage onNavigate={handleNavigate} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage onNavigate={handleNavigate} />} />
          <Route path="/reset-password" element={<ResetPasswordPage onNavigate={handleNavigate} />} />
          <Route path="/email-verification" element={<EmailVerificationPage />} />


          {/* --- Protected Routes --- */}
          {/* ── Recruiter + Professional routes — wrapped in DashboardLayout ── */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardGate />} />
            <Route path="/pricing" element={<SubscriptionPricingPage onNavigate={handleNavigate} />} />
            <Route path="/pricing/payment" element={<SubscriptionPaymentPage onNavigate={handleNavigate} />} />
            <Route path="/notifications" element={<NotificationsCenterPage />} />
            <Route path="/account-settings" element={<AccountSettingsPage />} />

            {/* Professional */}
            <Route path="/manage-profile" element={<ProtectedRoute requiredUserType="professional"><ManageProfilePage /></ProtectedRoute>} />
            <Route path="/job-application/:jobId" element={<ProtectedRoute requiredUserType="professional"><JobApplicationForm /></ProtectedRoute>} />
            <Route path="/my-applications" element={<ProtectedRoute requiredUserType="professional"><MyApplicationsPage /></ProtectedRoute>} />
            <Route path="/professional/recommendations" element={<ProtectedRoute requiredUserType="professional"><RecommendationsPage /></ProtectedRoute>} />
            <Route path="/saved-jobs" element={<ProtectedRoute requiredUserType="professional"><SavedJobsPage /></ProtectedRoute>} />
            <Route path="/verification" element={<ProtectedRoute requiredUserType="professional"><VerificationStatusPage /></ProtectedRoute>} />
            <Route path="/job-search" element={<JobSearchPage onNavigate={handleNavigate} />} />
            <Route path="/job-details/:jobId" element={<JobDetailsPage />} />
            <Route path="/job-details/:slug/:jobId" element={<JobDetailsPage />} />

            {/* Recruiter */}
            <Route path="/post-job" element={<ProtectedRoute requiredUserType="recruiter"><PostJobWizard /></ProtectedRoute>} />
            <Route path="/manage-jobs" element={<ProtectedRoute requiredUserType="recruiter"><ManageJobListingsPage onNavigate={handleNavigate} /></ProtectedRoute>} />
            <Route path="/recruiter/recommendations" element={<ProtectedRoute requiredUserType="recruiter"><RecruiterRecommendationsPage /></ProtectedRoute>} />
            <Route path="/recruiter/send-recommendation" element={<ProtectedRoute requiredUserType="recruiter"><SendRecommendationPage /></ProtectedRoute>} />
            <Route path="/find-professionals" element={<ProtectedRoute requiredUserType="recruiter"><ProfessionalSearchPage /></ProtectedRoute>} />
            <Route path="/view-applications/:jobId" element={<ProtectedRoute requiredUserType="recruiter"><ViewApplicationsPage /></ProtectedRoute>} />
            <Route path="/candidate-profile/:candidateId" element={<ProtectedRoute requiredUserType="recruiter"><CandidateProfilePage onNavigate={handleNavigate} /></ProtectedRoute>} />
            <Route path="/company-profile" element={<ProtectedRoute requiredUserType="recruiter"><CompanyProfilePage /></ProtectedRoute>} />
            <Route path="/subscription/pending" element={<ProtectedRoute><SubscriptionPendingPage /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
            <Route path="/subscription/stripe-checkout" element={<ProtectedRoute><StripePaymentPage /></ProtectedRoute>} />
            <Route path="/subscription/stripe-success" element={<ProtectedRoute><StripePaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          </Route>

          {/* Standalone routes (no sidebar) */}

          <Route path="/admin" element={
            <ProtectedRoute requiredUserType="admin">
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="users/:id" element={<AdminUserDetailsPage />} />
            <Route path="audit" element={<AdminAuditTrail />} />
            <Route path="audit/:id" element={<AdminAuditTrailDetailsPage />} />
            <Route path="audit/me" element={<AdminRecentActivity />} />
            <Route path="audit/stats" element={<AdminAuditStatistics />} />
            <Route path="jobs" element={<JobListingManagementPage />} />
            <Route path="jobs/:id" element={<AdminJobDetails />} />
            <Route path="job-applications" element={<AdminJobApplicationPage />} />
            <Route path="job-applications/:id" element={<AdminJobApplicationDetailPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsListPage />} />
            <Route path="subscriptions/:id" element={<AdminSubscriptionDetails />} />
            <Route path="subscription-plans" element={<AdminSubscriptionPlans />} />
            <Route path="subscription-plans/:id" element={<AdminSubscriptionPlanDetails />} />
            <Route path="subscription-payments" element={<AdminPaymentsPage />} />
            <Route path="subscription-payments/:id" element={<AdminPaymentDetailsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="notifications" element={<NotificationsCenterPage />} />
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {/* {isDashboardPage() && user ? <DashboardFooter /> : <Footer />} */}
      {(isAdminPage() && user) || (isChatPage()) ? (
        // Admin pages typically don't show footer
        <></>
      ) : <Footer />
        // isDashboardPage() && user ? (
        //   <DashboardFooter />
        // ) : (
        //   <Footer />
        // )
      }

    </div>
  );
}

export default App;
